import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, MagnifyingGlass, Bell, House, Buildings, Confetti, MusicNotes, CaretDown, ShieldCheck, CreditCard, Tag, Headset } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fileUrl, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/site";
import { PropertyCard } from "@/components/PropertyCard";
import { SkeletonCard } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";

const CAT_ICONS = { Farmhouses: House, Resorts: Buildings, "Banquet Halls": Confetti, "Function Halls": MusicNotes };

const Row = ({ title, items, favIds, onFav, loading }) => (
  <section className="mt-6">
    <div className="mb-3 flex items-center justify-between px-4">
      <h2 className="font-heading text-lg font-semibold text-stone-900">{title}</h2>
    </div>
    <div className="no-scrollbar flex gap-4 overflow-x-auto px-4 pb-2">
      {loading
        ? [1, 2, 3].map((i) => <SkeletonCard key={i} />)
        : items.length === 0
        ? <p className="px-1 text-sm text-stone-400">No properties in this city yet.</p>
        : items.map((p) => (
            <PropertyCard key={p.id} property={p} isFavorite={favIds.includes(p.id)} onToggleFavorite={onFav} className="min-w-[270px] max-w-[270px]" />
          ))}
    </div>
  </section>
);

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(localStorage.getItem("avb_city") || "Hyderabad");
  const [popular, setPopular] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [weekend, setWeekend] = useState([]);
  const [deals, setDeals] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [offers, setOffers] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const loadFavs = useCallback(() => {
    if (user?.role === "customer") api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
  }, [user]);

  const loadSections = useCallback(() => {
    setLoading(true);
    const c = `city=${encodeURIComponent(city)}`;
    Promise.all([
      api.get(`/properties?section=featured&${c}`),
      api.get(`/properties?section=popular&${c}`),
      api.get(`/properties?section=top_rated&${c}`),
      api.get(`/properties?sort=newest&${c}`),
      api.get(`/properties?section=best_deals&${c}`),
    ])
      .then(([f, p, t, w, d]) => {
        setFeatured(f.data); setPopular(p.data); setTopRated(t.data); setWeekend(w.data.slice(0, 8)); setDeals(d.data);
      })
      .finally(() => setLoading(false));
  }, [city]);

  useEffect(() => { loadSections(); }, [loadSections]);
  useEffect(() => { api.get(`/offers?city=${encodeURIComponent(city)}`).then((r) => setOffers(r.data)).catch(() => {}); }, [city]);
  useEffect(() => { loadFavs(); }, [loadFavs]);
  useEffect(() => {
    if (user) api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, [user]);
  useEffect(() => { track("home_visit"); }, []);

  const changeCity = (c) => { setCity(c); localStorage.setItem("avb_city", c); };
  const cityMeta = cities.find((c) => c.name === city) || {};

  const toggleFav = async (pid) => {
    if (!user) return navigate("/login");
    const r = await api.post(`/favorites/${pid}`);
    setFavIds((prev) => (r.data.favorited ? [...prev, pid] : prev.filter((x) => x !== pid)));
  };

  return (
    <div>
      <Seo path="/" jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "Avinya Bookings", url: (typeof window !== "undefined" ? window.location.origin : "") }} />
      <header className="sticky top-0 z-30 bg-white/80 px-4 pb-4 pt-5 backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <div>
            <Select value={city} onValueChange={changeCity}>
              <SelectTrigger data-testid="city-selector" className="h-auto w-auto gap-1 border-0 p-0 text-xs text-stone-500 shadow-none focus:ring-0">
                <MapPin size={13} weight="fill" className="text-primary" /> {city} <CaretDown size={11} />
              </SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c.id} value={c.name} data-testid={`city-opt-${c.name}`}>{c.name} <span className="text-xs text-stone-400">· {c.state}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button data-testid="notifications-btn" onClick={() => navigate(user ? "/notifications" : "/login")} className="relative rounded-full border border-stone-200 p-2.5 hover:bg-accent">
            <Bell size={20} className="text-stone-700" />
            {unread > 0 && <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">{unread}</span>}
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); navigate(`/explore?q=${encodeURIComponent(search)}`); }}
          className="mt-4 flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-1"
        >
          <MagnifyingGlass size={20} className="text-stone-400" />
          <Input data-testid="home-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Where do you want to go?" className="border-0 shadow-none focus-visible:ring-0" />
        </form>
      </header>

      <div className="mx-4 mt-5 overflow-hidden rounded-3xl">
        <div className="relative">
          <img src={cityMeta.banner || "https://images.unsplash.com/photo-1583037189850-1921ae7c6c22?q=85&w=1200"} alt={`${city} banner`} className="h-48 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
          <div className="absolute bottom-0 p-5">
            <span className="mb-1 inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-medium text-white backdrop-blur">{cityMeta.state || "India"}</span>
            <h1 className="font-heading text-2xl font-bold text-white">Escape to {city}.</h1>
            <p className="text-sm text-white/90">{cityMeta.tagline || "Farmhouses, resorts & event spaces."}</p>
          </div>
        </div>
      </div>

      {!loading && offers.length > 0 && (
        <section className="mt-6" data-testid="offers-section">
          <div className="mb-3 flex items-center justify-between px-4">
            <h2 className="font-heading text-lg font-semibold text-stone-900">Best Deals & Offers</h2>
          </div>
          <div className="no-scrollbar flex gap-4 overflow-x-auto px-4 pb-2">
            {offers.map((p) => (
              <button key={p.id} data-testid={`offer-${p.id}`} onClick={() => navigate(`/property/${p.id}`)} className="min-w-[270px] max-w-[270px] overflow-hidden rounded-2xl border border-stone-200 bg-white text-left transition-shadow hover:shadow-md">
                <div className="relative">
                  <img src={fileUrl(p.images?.[0])} alt={p.name} className="h-40 w-full object-cover" />
                  <span className="absolute left-3 top-3 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white">{p.discount_pct}% OFF</span>
                </div>
                <div className="p-3">
                  <p className="truncate font-heading font-semibold text-stone-900">{p.name}</p>
                  <p className="text-xs text-stone-500">{p.category} · {p.location}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-heading text-lg font-bold text-primary">{formatINR(p.offer_price)}</span>
                    <span className="text-sm text-stone-400 line-through">{formatINR(p.original_price)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {!loading && featured.length > 0 && (
        <Row title={`Staff Favourites in ${city}`} items={featured} favIds={favIds} onFav={toggleFav} loading={false} />
      )}
      <Row title="Popular Near You" items={popular} favIds={favIds} onFav={toggleFav} loading={loading} />
      <Row title="Top Rated Properties" items={topRated} favIds={favIds} onFav={toggleFav} loading={loading} />
      <Row title="Recently Added" items={weekend} favIds={favIds} onFav={toggleFav} loading={loading} />
      <Row title="Best Deals" items={deals} favIds={favIds} onFav={toggleFav} loading={loading} />

      <section className="mt-10 px-4" data-testid="trust-section">
        <h2 className="font-heading text-xl font-bold text-stone-900">Why Book with Avinya?</h2>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[[ShieldCheck, "Verified Properties"], [CreditCard, "Secure Payments"], [Tag, "Clear Pricing"], [Headset, "Customer Support"]].map(([Icon, t]) => (
            <div key={t} className="rounded-2xl border border-stone-200 bg-white p-4">
              <Icon size={24} weight="duotone" className="text-primary" />
              <p className="mt-2 text-sm font-semibold text-stone-800">{t}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-4 mt-6 overflow-hidden rounded-3xl bg-stone-900 p-6 text-center" data-testid="home-list-cta">
        <h2 className="font-heading text-xl font-bold text-white md:text-2xl">Own a farmhouse, resort or hall?</h2>
        <p className="mt-1 text-sm text-stone-300">List it on Avinya Bookings and reach customers looking to book.</p>
        <Button data-testid="home-list-btn" onClick={() => navigate("/list-your-property")} className="mt-4 rounded-full bg-primary px-7 py-5 text-base hover:bg-green-800">List Your Property</Button>
      </section>
    </div>
  );
}
